<!-- components/common/NovaPoshtaDeliveryForm.vue -->
<template>
  <div class="space-y-4">
    <h3 class="text-lg font-semibold">Доставка</h3>

    <!-- тип доставки -->
    <div class="space-y-2">
      <label class="block text-sm text-gray-600 mb-1">Спосіб доставки*</label>
      <label class="flex items-center gap-2">
        <input
          v-model="deliveryType"
          type="radio"
          class="rounded"
          value="np_branch"
        />
        <span>У відділення або поштомат НП</span>
      </label>
      <label class="flex items-center gap-2">
        <input
          v-model="deliveryType"
          type="radio"
          class="rounded"
          value="np_courier"
        />
        <span>Адресна доставка НП</span>
      </label>
    </div>

    <!-- місто (асинхронний селект із пошуком) -->
    <div>
      <label class="block text-sm text-gray-600 mb-1">Місто*</label>
      <div class="relative">
        <input
          v-model="cityInput"
          class="w-full border rounded-lg px-3 py-2 pr-8"
          placeholder="Почніть вводити місто…"
          autocomplete="off"
          @focus="cityOpen = true"
          @blur="onCityBlur"
        />
        <div
          v-if="cityOpen && cityOptions.length"
          class="absolute z-10 mt-1 w-full max-h-64 overflow-auto rounded-lg border bg-white shadow"
        >
          <button
            v-for="c in cityOptions"
            :key="c.ref"
            type="button"
            class="w-full text-left px-3 py-2 hover:bg-gray-50"
            @mousedown.prevent="selectCity(c)"
          >
            {{ c.label }}
          </button>
        </div>
      </div>
      <p v-if="cityTouched && !cityValid" class="text-xs text-red-600 mt-1">
        Оберіть місто зі списку
      </p>
    </div>

    <!-- відділення/поштомат або адреса -->
    <div v-if="deliveryType === 'np_branch'">
      <label class="block text-sm text-gray-600 mb-1"
        >Відділення або поштомат НП*</label
      >
      <div class="relative">
        <input
          v-model="whInput"
          class="w-full border rounded-lg px-3 py-2 pr-8"
          placeholder="Почніть вводити номер/адресу відділення…"
          :disabled="!citySelected"
          autocomplete="off"
          @focus="whOpen = true"
          @blur="onWhBlur"
        />
        <div
          v-if="whOpen && whOptions.length"
          class="absolute z-10 mt-1 w-full max-h-64 overflow-auto rounded-lg border bg-white shadow"
        >
          <button
            v-for="w in whOptions"
            :key="w.ref"
            type="button"
            class="w-full text-left px-3 py-2 hover:bg-gray-50"
            @mousedown.prevent="selectWh(w)"
          >
            {{ w.label }}
          </button>
        </div>
      </div>
      <p v-if="whTouched && !whValid" class="text-xs text-red-600 mt-1">
        Оберіть відділення/поштомат
      </p>
    </div>

    <div v-else>
      <label class="block text-sm text-gray-600 mb-1"
        >Адреса доставки НП*</label
      >
      <input
        v-model.trim="addrInput"
        class="w-full border rounded-lg px-3 py-2"
        placeholder="вул. Прикладна, 1, кв. 2"
        @blur="addrTouched = true"
      />
      <p v-if="addrTouched && !addrValid" class="text-xs text-red-600 mt-1">
        Вкажіть адресу доставки
      </p>
    </div>
  </div>
</template>

<script setup>
// 🔵 Моки НП (пізніше легко замінити на реальне API)
// import { useNovaPoshtaMock } from "~/composables/useNovaPoshtaMock";
import { ref, computed, watch, onMounted } from "vue";
import { useNovaPoshta } from "~/composables/useNovaPoshta";

const syncing = ref(false);
const initialized = ref(false);
const lastEmitted = ref("");

const props = defineProps({
  /* v-model */
  modelValue: {
    type: Object,
    default: () => ({
      deliveryType: "np_branch",
      city: "",
      cityRef: "",
      line1: "",
      warehouseRef: "",
      warehouseName: "",
    }),
  },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(["update:modelValue", "valid-change"]);

// 🔵 локальний стан
const deliveryType = ref(props.modelValue.deliveryType || "np_branch");

// місто
const { searchCities, searchWarehouses } = useNovaPoshta();
const cityInput = ref(props.modelValue.city || "");
const cityOptions = ref([]);
const citySelected = ref(
  props.modelValue.cityRef
    ? { ref: props.modelValue.cityRef, label: props.modelValue.city || "" }
    : null,
);
const cityOpen = ref(false);
const cityTouched = ref(false);
let cityDebounce;

async function searchCity(q) {
  cityOptions.value = await searchCities(q);
}
watch(cityInput, (v) => {
  clearTimeout(cityDebounce);
  const same =
    (citySelected.value?.label || "").toLowerCase() ===
    (v || "").trim().toLowerCase();
  if (!same && citySelected.value) citySelected.value = null;
  cityDebounce = setTimeout(() => searchCity(v || ""), 250);
});
function selectCity(c) {
  citySelected.value = c;
  cityInput.value = c.label;
  whSelected.value = null;
  whInput.value = "";
  whOptions.value = [];
  cityOpen.value = false;
}
async function onCityBlur() {
  const txt = (cityInput.value || "").trim();
  if (txt) {
    await searchCity(txt);
    const exact = cityOptions.value.find(
      (c) => c.label.toLowerCase() === txt.toLowerCase(),
    );
    citySelected.value = exact || null;
  } else {
    citySelected.value = null;
  }
  setTimeout(() => {
    cityOpen.value = false;
    cityTouched.value = true;
  }, 100);
}
const cityValid = computed(() => Boolean(citySelected.value?.ref));

// відділення
const whInput = ref(props.modelValue.warehouseName || "");
const whOptions = ref([]);
const whSelected = ref(
  props.modelValue.warehouseRef
    ? {
        ref: props.modelValue.warehouseRef,
        label: props.modelValue.warehouseName || "",
        type: "branch",
      }
    : null,
);
const whOpen = ref(false);
const whTouched = ref(false);
let whDebounce;
watch([whInput, citySelected, deliveryType], ([v]) => {
  clearTimeout(whDebounce);
  const sameWh =
    (whSelected.value?.label || "").toLowerCase() ===
    (v || "").trim().toLowerCase();
  if (!sameWh && whSelected.value) whSelected.value = null;
  whDebounce = setTimeout(async () => {
    if (!citySelected.value?.ref) {
      whOptions.value = [];
      return;
    }
    whOptions.value = await searchWarehouses(
      citySelected.value.ref,
      v || "",
      "all",
    );
  }, 250);
});
function selectWh(w) {
  whSelected.value = w;
  whInput.value = w.label;
  whOpen.value = false;
  whTouched.value = false;
}
async function onWhBlur() {
  const txt = (whInput.value || "").trim();
  if (txt && citySelected.value?.ref) {
    const list = await searchWarehouses(citySelected.value.ref, txt, "all");
    const exact = list.find((w) => w.label.toLowerCase() === txt.toLowerCase());
    whSelected.value = exact || null;
  } else {
    whSelected.value = null;
  }
  setTimeout(() => {
    whOpen.value = false;
    whTouched.value = true;
  }, 100);
}

// адреса (кур’єр)
const addrInput = ref(props.modelValue.line1 || "");
const addrTouched = ref(false);
const addrValid = computed(() =>
  deliveryType.value === "np_courier"
    ? (addrInput.value || "").trim().length >= 5
    : true,
);

// 🔵 загальна валідність
const whTextMatches = computed(
  () =>
    (whSelected.value?.label || "").trim().toLowerCase() ===
    (whInput.value || "").trim().toLowerCase(),
);
const whValid = computed(() =>
  deliveryType.value === "np_branch"
    ? Boolean(whSelected.value?.ref) && whTextMatches.value
    : true,
);
const valid = computed(
  () =>
    cityValid.value &&
    (deliveryType.value === "np_branch" ? whValid.value : addrValid.value),
);

// 🔵 емітимо value + валідність
function syncFromModel(v = {}) {
  deliveryType.value = v.deliveryType || "np_branch";
  cityInput.value = v.city || "";
  citySelected.value = v.cityRef
    ? { ref: v.cityRef, label: v.city || "" }
    : null;

  whInput.value = v.warehouseName || "";
  whSelected.value = v.warehouseRef
    ? { ref: v.warehouseRef, label: v.warehouseName || "", type: "branch" }
    : null;

  addrInput.value = v.line1 || "";
}
watch(
  () => props.modelValue,
  (val) => {
    syncing.value = true;
    syncFromModel(val || {});
    syncing.value = false;
    if (!initialized.value) initialized.value = true;
  },
  { immediate: true, deep: true },
);

// 🔵 емітимо value + валідність (без рекурсій)
watch(
  [deliveryType, citySelected, addrInput, whSelected, cityInput, whInput],
  () => {
    // не емітити під час початкової синхронізації або коли батько змінює modelValue
    if (!initialized.value || syncing.value) return;

    const value = {
      deliveryType: deliveryType.value,
      city: citySelected.value?.label || cityInput.value || "",
      cityRef: citySelected.value?.ref || "",
      line1: deliveryType.value === "np_courier" ? addrInput.value : "",
      warehouseRef:
        deliveryType.value === "np_branch" ? whSelected.value?.ref || "" : "",
      warehouseName:
        deliveryType.value === "np_branch"
          ? whSelected.value?.label || whInput.value || ""
          : "",
    };

    // емімо тільки якщо реально є зміни
    const snap = JSON.stringify(value);
    if (snap === lastEmitted.value) return;
    lastEmitted.value = snap;

    emit("update:modelValue", value);
    emit("valid-change", valid.value);
  },
  // 🔵 без immediate, бо перший стан підтягнемо через watch(props.modelValue)
);

// 🔵 ініціалізація списків (якщо модель уже з профілю)
onMounted(async () => {
  if (cityInput.value) await searchCity(cityInput.value);
  if (citySelected.value?.ref && whInput.value) {
    whOptions.value = await searchWarehouses(
      citySelected.value.ref,
      whInput.value,
      "all",
    );
  }
});
</script>
